import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, PopConfirm, Select, Table } from '@maxtropy/components';
import {
  apiV2DistributionCabinetMonitorListPost,
  apiV2DistributionCabinetRoomMonitorSavePost,
  apiV2DistributionCabinetRoomMonitorPagePost,
} from '@maxtropy/device-customer-apis-v2';
import { PlusOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import { ColumnsType } from 'antd/es/table';
import { v4 as uuidv4 } from 'uuid';
import { MonitorItem, VideoAccessType } from '../../utils';
import { Spin } from 'antd';

interface AddVideoModalProps {
  onClose: () => void;
  updateFn: () => void;
  visible: boolean;
  room?: any;
}

type monitorOptions = {
  label: string;
  value: string;
};

const AddVideoModal: FC<AddVideoModalProps> = ({ room, onClose, updateFn, visible }) => {
  const [form] = Form.useForm();
  const [monitorList, setMonitorList] = useState<MonitorItem[]>([]);
  const [selectedMonitors, setSelectedMonitors] = useState<monitorOptions[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    apiV2DistributionCabinetMonitorListPost({ type: VideoAccessType.SWS, roomId: room.id }).then(res => {
      setMonitorList(res.list ?? []);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    apiV2DistributionCabinetRoomMonitorPagePost({ roomId: room.id, page: 1, size: 500 })
      .then(res => {
        setSelectedMonitors((res.list ?? []).map(item => ({ label: item.name!, value: item.code! })));
        form.setFieldsValue({
          list: res.list ?? [],
        });
      })
      .finally(() => setLoading(false));
  }, [room]);

  // 弹框确认
  const onOk = () => {
    form.validateFields().then(values => {
      apiV2DistributionCabinetRoomMonitorSavePost({
        roomId: room?.id,
        monitorList: selectedMonitors.map(item => ({
          type: VideoAccessType.SWS,
          code: item.value,
          name: item.label,
        })),
      }).then(() => {
        onClose();
      });
    });
  };

  const addRow = () => {
    const newRow = { id: uuidv4() };
    const prevList = form.getFieldValue('list') || [];
    prevList.push(newRow);
    form.setFieldsValue({ list: prevList });
  };

  const getAvailableMonitor = useMemo(
    () => (index: number) => {
      return monitorList
        .filter(
          item => !selectedMonitors.find((selectedMonitor, idx) => idx !== index && selectedMonitor.value === item.code)
        )
        .map(monitor => ({
          value: monitor.code,
          label: monitor.name,
        }));
    },
    [selectedMonitors, monitorList]
  );

  // 处理监控选择变化
  const handleMonitorChange = (option: monitorOptions, index: number) => {
    const newSelected = [...selectedMonitors];
    newSelected[index] = option;

    setSelectedMonitors(newSelected);
  };

  const columns: ColumnsType<MonitorItem> = useMemo(
    () => [
      {
        title: <div className={styles.requiredLabel}>接入方式</div>,
        dataIndex: 'type',
        render(value, record, index) {
          return (
            <Form.Item
              name={['list', index, 'type']}
              rules={[{ required: true, message: '请选择' }]}
              style={{ marginBottom: 0 }}
            >
              <Select
                placeholder="请输入"
                allowClear
                style={{ width: '100%' }}
                options={[{ value: VideoAccessType.SWS, label: '外高桥视频接入' }]}
              />
            </Form.Item>
          );
        },
      },
      {
        title: <div className={styles.requiredLabel}>监控视频名称</div>,
        dataIndex: 'code',
        render(value, record, index) {
          console.log('code', form.getFieldValue(['list', index, 'code']));
          return (
            <Form.Item
              name={['list', index, 'code']}
              rules={[{ required: true, message: '请输入/选择' }]}
              style={{ marginBottom: 0 }}
            >
              <Select
                placeholder="请输入/选择"
                allowClear
                showSearch
                style={{ width: '100%' }}
                optionFilterProp="label"
                onChange={(value, option) => handleMonitorChange(option as unknown as monitorOptions, index)}
                options={getAvailableMonitor(index)}
              />
            </Form.Item>
          );
        },
      },
      {
        title: '操作',
        ellipsis: true,
        width: '15%',
        render(value, record, index) {
          return (
            <PopConfirm
              placement="top"
              title={'是否确认删除?'}
              onConfirm={() => {
                const prevList = form.getFieldValue('list');

                const removeIndex = prevList.findIndex((item: MonitorItem) => {
                  return item.code === record.code;
                });
                prevList.splice(removeIndex, 1);
                form.setFieldsValue({ list: prevList });
                selectedMonitors.splice(removeIndex, 1);
                setSelectedMonitors(selectedMonitors);
              }}
            >
              <Button type="link">删除</Button>
            </PopConfirm>
          );
        },
      },
    ],
    [monitorList, selectedMonitors]
  );

  return (
    <Modal title="编辑视频监控" open={visible} onOk={onOk} maskClosable={false} onCancel={onClose}>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" isOrigin={true}>
          <div style={{ color: 'var(--mx-form-item-label-color)' }}>监控视频列表</div>
          <Form.Item name="list" valuePropName="dataSource" noStyle>
            <Table style={{ marginTop: 8 }} rowKey="code" columns={columns} />
          </Form.Item>
          <Button
            onClick={addRow}
            type="dashed"
            wrapClassName={styles.addRow}
            className={styles.rowInnerBtn}
            ghost
            icon={<PlusOutlined />}
          >
            新增视频监控
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddVideoModal;
